import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Avatar,
  Paper,
  IconButton,
  Stack,
  Fade,
  useTheme,
  alpha,
} from '@mui/material';
import {
  CloudUpload,
  CameraAlt as Camera,
  Delete,
  Save,
  PhotoCamera,
  Cancel,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';

export default function ProfilePhoto() {
  const theme = useTheme();
  const [dragActive, setDragActive] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [savedPhoto, setSavedPhoto] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const token = localStorage.getItem('jwttoken');

  useEffect(() => {
    axios
      .get('https://tdtlworld.com/dss-kaas-backend/lms/user/', {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        setSavedPhoto(response.data.profile_pic);
      })
      .catch((error) => console.error('Error fetching profile photo:', error));
  }, [token]);

  const handleDrag = (e) => {
    e.preventDefault();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleFileUpload = (file) => {
    if (!file.type.startsWith('image/')) {
      Swal.fire('Invalid File', 'Please upload a valid image file.', 'error');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setSelectedPhoto(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSave = () => {
    axios
      .patch('https://tdtlworld.com/dss-kaas-backend/lms/user/', 
      { profile_pic: selectedPhoto }, 
      { headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        setSavedPhoto(selectedPhoto);
        setSelectedPhoto(null);
        Swal.fire({
          title: 'Success!',
          text: 'Profile photo updated successfully.',
          icon: 'success',
          confirmButtonColor: '#D4AF37',
        });
      })
      .catch((error) => {
        console.error('Error saving profile photo:', error);
        Swal.fire('Error', 'Failed to save profile photo.', 'error');
      });
  };

  const handleStartCamera = () => {
    setCameraActive(true);
    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then((stream) => {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      })
      .catch((err) => {
        console.error('Error accessing camera:', err);
        Swal.fire('Error', 'Failed to access camera.', 'error');
      });
  };

  const handleCapturePhoto = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    const context = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataURL = canvas.toDataURL('image/png');
    setSelectedPhoto(dataURL);
    stopCamera();
  };

  const stopCamera = () => {
    const stream = videoRef.current.srcObject;
    if (stream) stream.getTracks().forEach((track) => track.stop());
    setCameraActive(false);
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 4, textAlign: 'center' }}>
        Profile Photo
      </Typography>

      <Stack spacing={4} alignItems="center">
        {/* Current Photo */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="subtitle2" color="text.secondary" fontWeight={700} sx={{ mb: 2 }}>
            CURRENT PHOTO
          </Typography>
          <Avatar
            src={savedPhoto}
            sx={{
              width: 150,
              height: 150,
              border: '4px solid',
              borderColor: 'primary.main',
              boxShadow: theme.shadows[4],
            }}
          />
        </Box>

        {/* Upload Area */}
        <Paper
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          sx={{
            width: '100%',
            p: 4,
            textAlign: 'center',
            border: '2px dashed',
            borderColor: dragActive ? 'primary.main' : 'divider',
            bgcolor: dragActive ? alpha(theme.palette.primary.main, 0.05) : 'background.paper',
            borderRadius: '16px',
            transition: '0.3s',
            cursor: 'pointer',
          }}
          component="label"
        >
          <input type="file" hidden accept="image/*" onChange={handleChange} />
          <CloudUpload sx={{ fontSize: '3rem', color: 'primary.main', mb: 1 }} />
          <Typography variant="body1" fontWeight={600}>
            Drop your image here or <Box component="span" sx={{ color: 'primary.main' }}>select a file</Box>
          </Typography>
        </Paper>

        <Typography variant="body2" color="text.secondary">- OR -</Typography>

        <Button
          variant="outlined"
          startIcon={<PhotoCamera />}
          onClick={handleStartCamera}
          sx={{ borderRadius: '50px', px: 4 }}
        >
          Take Live Photo
        </Button>

        {cameraActive && (
          <Fade in={cameraActive}>
            <Box sx={{ width: '100%', textAlign: 'center' }}>
              <video ref={videoRef} style={{ borderRadius: '16px', width: '100%', marginBottom: '16px' }} />
              <Stack direction="row" spacing={2} justifyContent="center">
                <Button variant="contained" color="success" onClick={handleCapturePhoto}>Capture</Button>
                <Button variant="outlined" color="error" onClick={stopCamera}>Cancel</Button>
              </Stack>
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </Box>
          </Fade>
        )}

        {selectedPhoto && (
          <Fade in={!!selectedPhoto}>
            <Box sx={{ width: '100%', textAlign: 'center', p: 3, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: '16px' }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>PREVIEW</Typography>
              <Avatar
                src={selectedPhoto}
                sx={{ width: 120, height: 120, mx: 'auto', mb: 3, border: '4px solid', borderColor: 'success.main' }}
              />
              <Stack direction="row" spacing={2} justifyContent="center">
                <Button variant="contained" startIcon={<Save />} onClick={handleSave}>Save Photo</Button>
                <Button variant="outlined" startIcon={<Delete />} color="error" onClick={() => setSelectedPhoto(null)}>Clear</Button>
              </Stack>
            </Box>
          </Fade>
        )}
      </Stack>
    </Box>
  );
}
